import type { ThemeConfig } from '@erikdakoda/site-template-ui/types';
import themeConfig from '@erikdakoda/site-template-ui/config';
import { faIconConfig } from '@erikdakoda/ui/shared/faIconConfig';
import type { IconPrefix } from '@erikdakoda/ui/components/FaIcon';
import { alpha, darken, rgbToHex } from '@mui/material/styles';
import { adjustLightAndSat, addOpacity } from '@erikdakoda/ui/shared/colorUtils';
import type { Mode } from '@erikdakoda/site-template-ui/types';
import type { Settings } from '@erikdakoda/site-template-ui/context/settingsContext';
import type { Theme } from '@mui/material/styles';
import type { ThemeOptions } from '@mui/material/styles';
import type { OwnerStateThemeType } from '@erikdakoda/site-template-ui/theme/overrides';
import typography from './typography';
import { FORGENTIC_PINNED_CONNECTORS } from './pinnedConnectors';
import ForgenticLogo from '../components/ForgenticLogo';
import ForgenticMark from '../components/ForgenticMark';

const backColor = '#710000';

const config: ThemeConfig = {
  // ** Layout Configs
  layout: 'vertical',
  mode: 'dark' as Mode,
  direction: 'ltr',
  skin: 'default',
  contentWidth: 'full',
  footer: 'hidden',

  // ** Routing Configs
  routingLoader: true,

  // ** Palette Configs
  lightColor: rgbToHex('rgb(227, 227, 227)'),
  darkColor: rgbToHex('rgb(58, 58, 58)'),
  primaryColor: '#B87D5B',
  secondaryColor: '#F3F440',
  backColor,
  backTheme: 'midnight-static',

  // ** Navigation (Menu) Configs
  navHidden: false,
  navBelow: false,
  menuTextTruncate: true,
  menuTextHidden: false,
  navSubItemIcon: 'circle-outline',
  verticalNavToggleType: 'accordion',
  navCollapsed: false,
  navigationSize: 220,
  collapsedNavigationSize: 65,
  contentPadding: 4,
  afterVerticalNavMenuContentPosition: 'fixed',
  beforeVerticalNavMenuContentPosition: 'static',
  horizontalMenuAnimation: true,

  // ** AppBar Configs
  appBar: 'hidden',
  backgroundFilter: 'blur(8px)',

  // ** Other Configs
  responsiveFontSizes: true,
  disableRipple: false,
  disableCustomizer: true,
  fontAwesomePrefix: 'fal',
  toastPosition: 'top-right',
  branding: ForgenticLogo,
  brandMark: ForgenticMark,
  pinnedConnectors: FORGENTIC_PINNED_CONNECTORS,
};

const options: (theme: Theme, settings: Settings) => ThemeOptions = (theme, settings) => {
  const paperColor = alpha(theme.palette?.customColors?.opposite || '#000', 0.3);
  const paperBorder = {
    border: '1px solid rgba(255, 255, 255, 0.15)',
    borderBottom: '1px solid rgba(0, 0, 0, 0.25)',
    borderRight: '1px solid rgba(0, 0, 0, 0.25)',
  };

  return {
    components: {
      MuiPaper: {
        styleOverrides: {
          root: paperBorder,
          outlined: {
            borderBottom: '1px solid rgba(255, 255, 255, 0.15)',
            borderRight: '1px solid rgba(255, 255, 255, 0.15)',
          },
        },
      },
      MuiDataGrid: {
        defaultProps: {
          // Float the vertical scrollbar over the pinned columns instead of reserving a strip
          // beside them. The grid measures the real scrollbar and writes a matching `right` offset
          // inline on every pinned cell, so browsers set to always show scrollbars leave a gap
          // between the last pinned column and the right edge. Telling the grid the scrollbar is
          // zero-width lets it compute those offsets from the true edge, and it still spaces
          // multiple pinned columns correctly.
          scrollbarSize: 0,
        },
        styleOverrides: {
          root: {
            // Blur lives on a pseudo-element child so the grid root is not a backdrop root;
            // otherwise the sticky header/pinned-cell blurs below sample nothing (Chrome 152+).
            position: 'relative',
            isolation: 'isolate',
            // The pseudo-element paints the pane; without this the grid's own default paper
            // background paints it a second time and the whole grid reads twice as opaque.
            backgroundColor: 'transparent',
            ...paperBorder,
            '&::before': {
              content: '""',
              position: 'absolute',
              inset: 0,
              zIndex: -1,
              borderRadius: 'inherit',
              backgroundColor: paperColor,
              backdropFilter: theme.palette.background.filter,
            },
            '& .MuiDataGrid-root': {
              position: 'relative',
              isolation: 'isolate',
              backgroundColor: 'transparent',
              ...paperBorder,
              '&::before': {
                content: '""',
                position: 'absolute',
                inset: 0,
                zIndex: -1,
                borderRadius: 'inherit',
                backgroundColor: paperColor,
                backdropFilter: theme.palette.background.filter,
              },
            },
            '& .MuiDataGrid-scrollShadow': {
              backgroundColor: 'transparent !important',
              backdropFilter: 'none !important',
            },
            '& .MuiDataGrid-iconButtonContainer .MuiIconButton-root:not(:hover)': {
              backgroundColor: 'transparent !important',
            },
          },
          detailPanel: ({ theme }: OwnerStateThemeType) => ({
            backgroundColor: theme.palette.background.paper,
          }),
          panelContent: {
            backdropFilter: theme.palette.background.filter,
            '& .MuiFormControl-root': {
              marginBottom: 0,
            },
          },
          // Sticky surfaces read `background.header` off the *merged* theme, so they must use the
          // callback form. A plain object would close over the theme passed into `options`, which
          // predates the palette below and still holds the package default.
          columnHeaders: ({ theme }: OwnerStateThemeType) => ({
            backgroundColor: 'transparent !important',
            '& .MuiDataGrid-filler': {
              backgroundColor: `${theme.palette.background.header} !important`,
              backdropFilter: theme.palette.background.filter,
            },
          }),
          columnHeaderRow: {
            backgroundColor: 'transparent !important',
          },
          columnHeader: ({ theme }: OwnerStateThemeType) => ({
            backgroundColor: `${theme.palette.background.header} !important`,
            backdropFilter: theme.palette.background.filter,
            '& .MuiDataGrid-iconButtonContainer': {
              backgroundColor: 'transparent !important',
              '& .MuiIconButton-root:not(:hover)': {
                backgroundColor: 'transparent !important',
              },
            },
          }),
          scrollbarFiller: ({ theme }: OwnerStateThemeType) => ({
            backgroundColor: `${theme.palette.background.header} !important`,
            backdropFilter: theme.palette.background.filter,
          }),
          scrollShadow: {
            backgroundColor: 'transparent !important',
            backdropFilter: 'none !important',
          },
          footerContainer: {
            '& > div:not(.data-grid-pagination)': {
              display: 'none',
            },
          },
          overlay: {
            '& .MuiLinearProgress-root': {
              height: '2px',
            },
          },
          toolbar: {
            padding: `0 ${theme.spacing(2)}`,
          },
          'cell--pinnedLeft': ({ theme }: OwnerStateThemeType) => ({
            backgroundColor: theme.palette.background.header,
            backdropFilter: theme.palette.background.filter,
          }),
          'cell--pinnedRight': ({ theme }: OwnerStateThemeType) => ({
            backgroundColor: theme.palette.background.header,
            backdropFilter: theme.palette.background.filter,
          }),
          'filler--pinnedLeft': ({ theme }: OwnerStateThemeType) => ({
            backgroundColor: theme.palette.background.header,
            backdropFilter: theme.palette.background.filter,
          }),
          'filler--pinnedRight': ({ theme }: OwnerStateThemeType) => ({
            backgroundColor: theme.palette.background.header,
            backdropFilter: theme.palette.background.filter,
          }),
          cell: {
            '& .hover-item': {
              opacity: 0,
              transition: [
                theme.transitions.create(['opacity'], {
                  easing: theme.transitions.easing.easeOut,
                  duration: theme.transitions.duration.standard,
                }),
              ],
            },
            '&:hover .hover-item': {
              opacity: 1,
            },
          },
          row: {
            '&.even': {
              backgroundColor: 'transparent',
              '&:hover': {
                backgroundColor: theme.palette.action.hover,
              },
              '&.Mui-selected': {
                backgroundColor: theme.palette.action.selected,
              },
            },
            '&.odd': {
              backgroundColor: 'transparent',
              '&:hover': {
                backgroundColor: theme.palette.action.hover,
              },
              '&.Mui-selected': {
                backgroundColor: theme.palette.action.selected,
              },
            },
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          anchorLeft: {
            '& .MuiDrawer-paper': {
              borderTop: 'none',
              borderLeft: 'none',
              borderBottom: 'none',
            },
          },
          anchorRight: {
            '& .MuiDrawer-paper': {
              borderTop: 'none',
              borderRight: 'none',
              borderBottom: 'none',
            },
          },
        },
      },
      MuiListItemButton: {
        styleOverrides: {
          root: {
            '&.top-nav-link': {
              paddingTop: theme.spacing(2.25),
              paddingBottom: theme.spacing(2.25),
              borderRadius: 24,
              '&:hover': {
                background: theme.palette.action.hover,
              },
              '&:focus-visible': {
                outline: 0,
                backgroundColor: theme.palette.action.focus,
              },
              '&.Mui-selected, &.Mui-selected:hover': {
                boxShadow: 3,
                backgroundImage: `linear-gradient(180deg, ${alpha(
                  settings.mode === 'light' ? '#000' : '#FFF',
                  0.2,
                )}, ${alpha(settings.mode === 'light' ? '#000' : '#FFF', 0)})`,
              },
            },
          },
        },
      },
      MuiTypography: {
        styleOverrides: {
          gutterBottom: {
            margin: '0px 0px 0.5em',
          },
        },
      },
    },
    mixins: {
      toolbar: {
        minHeight: 72,
      },
    },
    palette: {
      divider: darken(theme.palette?.customColors?.main || '#FFF', 0.7),
      background: {
        paper: paperColor,
        menu: addOpacity(adjustLightAndSat(backColor, 0.5, 1), 0.7),
        backdrop: alpha(theme.palette?.customColors?.main || '#FFF', 0.1),
        default: adjustLightAndSat(backColor, 0.15, 1),
        // Kept translucent enough that the backdrop blur on headers and pinned columns is visible;
        // at 0.9 the glass reads as flat paint.
        header: alpha(adjustLightAndSat(backColor, 0.35, 0.8), 0.5),
      },
    },
    typography,
  };
};

function register() {
  themeConfig.config = config;
  themeConfig.options = options;
  faIconConfig.prefix = config.fontAwesomePrefix as IconPrefix;
}

export default register;
